/**
 * Native Apple Sign In for iOS via Capacitor plugin.
 * On iOS: Triggers native ASAuthorizationController sheet.
 * On web: Not available (web uses Cognito Hosted UI redirect).
 *
 * Flow: native sheet → Apple JWT → POST /auth/apple-native → Cognito tokens → stored in localStorage
 */
import { registerPlugin } from '@capacitor/core';

const AppleSignIn = registerPlugin('AppleSignIn');

const API_URL = process.env.REACT_APP_API_URL || '';

/**
 * Trigger native Apple Sign In and authenticate with backend.
 * Returns { userId, email, name, tokens } on success.
 * Throws on cancel or failure.
 */
export async function signInWithApple() {
  // 1. Show native Apple Sign In sheet
  const result = await AppleSignIn.signIn();

  if (!result.identityToken) {
    throw new Error('No identity token received from Apple');
  }

  // 2. Send identity token to backend for verification + Cognito user creation
  const response = await fetch(`${API_URL}/auth/apple-native`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      identityToken: result.identityToken,
      fullName: result.fullName || null,
    }),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: 'Authentication failed' }));
    throw new Error(err.error || `Auth failed: ${response.status}`);
  }

  const authResult = await response.json();

  // 3. Store auth state in localStorage for the app to pick up
  localStorage.setItem('pomogrow_auth', JSON.stringify({
    userId: authResult.userId,
    email: authResult.email,
    name: authResult.name,
    idToken: authResult.idToken,
    accessToken: authResult.accessToken,
    refreshToken: authResult.refreshToken,
    expiresAt: Date.now() + 3600000, // 1 hour
  }));
  window.dispatchEvent(new Event('pomogrow:auth-change'));

  return authResult;
}

/**
 * Check if user has a stored auth session.
 */
export function getStoredAuth() {
  try {
    const raw = localStorage.getItem('pomogrow_auth');
    if (!raw) return null;
    const auth = JSON.parse(raw);
    if (auth.expiresAt && auth.expiresAt < Date.now()) {
      localStorage.removeItem('pomogrow_auth');
      return null;
    }
    return auth;
  } catch {
    return null;
  }
}

/**
 * Read the stored auth record without enforcing expiry. The refresh-token
 * scheduler needs this so it can grab the refreshToken even when the idToken
 * has just expired (in which case getStoredAuth() would delete the record).
 */
export function peekStoredAuth() {
  try {
    const raw = localStorage.getItem('pomogrow_auth');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/**
 * Clear stored auth session.
 */
export function signOut() {
  localStorage.removeItem('pomogrow_auth');
  window.dispatchEvent(new Event('pomogrow:auth-change'));
}
